import React, { ReactNode, useState, useEffect, useRef, useMemo } from 'react'
import { useTranslate } from '../hooks/useTranslate'
import { APP_VERSION } from '../constants'
import VersionHistoryPanel from './VersionHistoryPanel'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { UserProfile } from './auth/UserProfile'
import DisclaimerModal from './DisclaimerModal'
import { FaTimes, FaHospitalUser, FaInfoCircle, FaHistory, FaStethoscope, FaUserShield } from 'react-icons/fa'
import { useAuth } from '../contexts/AuthContext'
import { ADMIN_CONFIG } from '../config/env'
import { MdContentPasteSearch } from "react-icons/md"
import { FaSearchengin } from "react-icons/fa6"
import { TbFolderShare, TbExclamationMark, TbMessageReportFilled, TbReportAnalytics } from "react-icons/tb"
import { HiShare } from 'react-icons/hi'
import { AiFillFolderOpen } from "react-icons/ai"
import { DataSource, Folder } from '../types'
import { FileIcon } from './common/FileIcon'
import * as sharedReportService from '../services/sharedReportService'
import type { SaveSharedReportResponse } from '../types/shared'

// Add bookmark button styles
const bookmarkStyles = `
/* Smooth elegant search panel animation */
@keyframes smoothFadeSlide {
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0px);
  }
}

@keyframes smoothBackdrop {
  0% {
    opacity: 0;
    backdrop-filter: blur(0px);
  }
  100% {
    opacity: 1;
    backdrop-filter: blur(8px);
  }
}

.bookmarkBtn {
  font-size: 13px;
  font-family: inherit;
  font-weight: 700;
  padding: 2.7px;
  border-radius: 14px;
  cursor: pointer;
  border: none;
  color: black;
  box-shadow:
    rgba(0, 0, 0, 0.1) 0px 20px 25px -5px,
    rgba(0, 0, 0, 0.04) 0px 10px 10px -5px;
  background: linear-gradient(
    0deg,
    rgba(252, 165, 165, 1) 0%,
    rgba(252, 165, 165, 1) 47%,
    rgba(252, 165, 165, 1) 47%,
    rgba(252, 165, 165, 1) 100%
  );
  transition: all 0.1s ease-in-out;
}

.bookmarkBtn:active {
  transform: translateY(3px);
  box-shadow:
    rgba(0, 0, 0, 0.1) 0px 14px 25px -5px,
    rgba(0, 0, 0, 0.04) 0px 10px 10px -5px;
}

.bookmarkBtn .button-content {
  display: flex;
  align-items: center;
  background: white;
  padding: 0.5em 1.08em;
  padding-left: 0.86em;
  border-radius: 12px;
  gap: 5px;
}

.bookmarkBtn svg {
  width: 20px;
  height: 20px;
}

/* Data Doctor SVG Logo Styles */
/* 1. Main Container (Aligns Icon and Text Side-by-Side) */
.dd-logo-container {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 100%; /* Adapts to your header height */
  cursor: pointer;
  padding-right: 6px; /* Spacing from other elements */
  background: transparent;
  border: none;
  outline: none;
}
 
/* 2. The SVG Icon Size */
.dd-svg-icon {
  width: 55px !important; /* Wider icon */
  height: 40px !important;
  min-width: 55px !important;
  min-height: 40px !important;
  max-width: 55px !important;
  max-height: 40px !important;
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.2)) !important; /* Adds depth */
  transition: transform 0.3s ease !important;
}
 
/* 3. Text Styling */
.dd-text-group {
  display: flex;
  flex-direction: column; /* Stacks "Data" and "Doctor" tightly for a logo look */
  justify-content: center;
  line-height: 0.8; /* Very tight line height */
}
 
.dd-word-data {
  font-family: 'Arial Black', 'Segoe UI', sans-serif !important;
  font-weight: 900 !important;
  font-size: 18px !important; /* Much larger text */
  color: #ffffff !important; /* White to pop on Orange */
  letter-spacing: 0.3px !important;
}
 
.dd-word-doctor {
  font-family: 'Arial', 'Segoe UI', sans-serif !important;
  font-weight: 600 !important;
  font-size: 18px !important; /* Much larger text */
  color: #ffffff !important;
  letter-spacing: 0.5px !important;
}
 
/* 4. Hover Animation */
.dd-logo-container:hover .dd-svg-icon {
  transform: scale(1.1); /* Zooms in slightly */
}
 
/* Optional: Make the Red Pulse line glow on hover */
.dd-logo-container:hover .dd-svg-icon path[stroke="#ff3b3b"] {
  filter: drop-shadow(0 0 5px #ff3b3b);
}

.bookmarkBtn svg {
  height: 20px;
  margin-right: 4px;
  color: #ff0000;
  transition: transform 0.3s;
}

.bookmarkBtn:hover svg {
  transform: scale(1.2);
}

#search-wrap {
  margin: 0;
  display: inline-block;
  position: relative;
  height: 50px;
  float: left;
  padding: 0;
  min-width: 50px;
  margin-left: 2px;
}

/* Search Dropdown Animation Styles */
.search-dropdown {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  transform-origin: top center;
}

.search-dropdown.show {
  opacity: 1;
  transform: translateY(0) scale(1);
}

/* Search button hover animation */
.search-button-hover {
  transition: all 0.2s ease-in-out;
}

.search-button-hover:hover {
  transform: translateY(-1px);
  box-shadow: 
    rgba(0, 0, 0, 0.15) 0px 25px 30px -5px,
    rgba(0, 0, 0, 0.08) 0px 15px 15px -5px;
}

.search-input {
  height: 45px;
  font-size: 18px;
  display: inline-block;
  font-family: "Lato", Arial, sans-serif;
  font-weight: 100;
  border: none;
  outline: none;
  color: white !important;
  padding: 3px;
  padding-left: 15px;
  width: 0px;
  position: absolute;
  top: 2px;
  left: 0;
  background: transparent;
  z-index: 3;
  transition: width .4s cubic-bezier(0.000, 0.795, 0.000, 1.000);
  cursor: pointer;
  border-radius: 25px;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.search-input:focus:hover {
  border-bottom: 1px solid #BBB;
}

.search-input:focus {
  width: 300px;
  z-index: 1;
  border-bottom: 1px solid #BBB;
  cursor: text;
  color: white !important;
}

.search-input::placeholder {
  color: white !important;
  font-weight: bold;
  font-family: 'Heiti Regular', 'Heiti', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  animation: typing 3s steps(20, end) infinite;
}

@keyframes typing {
  0% { content: ''; }
  5% { content: 'S'; }
  10% { content: 'Se'; }
  15% { content: 'Sea'; }
  20% { content: 'Sear'; }
  25% { content: 'Searc'; }
  30% { content: 'Search'; }
  35% { content: 'Search '; }
  40% { content: 'Search y'; }
  45% { content: 'Search yo'; }
  50% { content: 'Search you'; }
  55% { content: 'Search your'; }
  60% { content: 'Search your '; }
  65% { content: 'Search your R'; }
  70% { content: 'Search your Re'; }
  75% { content: 'Search your Rep'; }
  80% { content: 'Search your Repo'; }
  85% { content: 'Search your Repor'; }
  90% { content: 'Search your Report'; }
  95% { content: 'Search your Reports'; }
  100% { content: 'Search your Reports here'; }
}

.search-input:focus::placeholder {
  color: white !important;
}

.search-submit {
  height: auto;
  width: auto;
  min-width: auto;
  padding: 2.7px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  color: black;
  float: right;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%);
  border: 1px solid #e5e7eb;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 14px;
  font-family: 'Montserrat', 'Alright Sans', 'Alright Sans Bold', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.025em;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.search-submit:hover {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f8fafc 100%);
  border-color: #cbd5e1;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.search-submit .button-content {
  display: flex;
  align-items: center;
  background: white;
  padding: 0.6em 1.5em;
  padding-left: 1.2em;
  border-radius: 12px;
  gap: 8px;
  min-width: 134px;
  justify-content: center;
}

.search-submit svg {
  width: 20px;
  height: 20px;
  margin-right: 4px;
  color: black;
  transition: transform 0.3s;
  display: inline;
}
`;

// Inject styles
if (typeof document !== 'undefined') {
    const styleId = 'bookmark-styles';
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = bookmarkStyles;
        document.head.appendChild(style);
    }
}

interface LayoutProps {
    children: ReactNode
    allDataSources?: DataSource[]
    folders?: Folder[]
    currentReportId?: string
    currentReportName?: string
}

const Layout = ({ children, allDataSources = [], folders = [] }: LayoutProps) => {
    const location = useLocation()
    const navigate = useNavigate()
    const { user } = useAuth()
    const [showDisclaimer, setShowDisclaimer] = useState(false)

    const { t } = useTranslate()

    // Check if current user belongs to admin group
    const isAdmin = useMemo(() => {
        if (!user?.email || !ADMIN_CONFIG.adminGroupName) return false;
        const userGroups = user?.groupNamesCommaSeparated || '';
        if (userGroups.length === 0) return false;
        const groupList = userGroups.split(',').map(g => g.trim());
        return groupList.includes(ADMIN_CONFIG.adminGroupName);
    }, [user?.email, user?.groupNamesCommaSeparated]);
    const [showVersion, setShowVersion] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const [isSearchFocused, setIsSearchFocused] = useState(false)
    const searchContainerRef = useRef<HTMLDivElement>(null)

    // Shared reports for search
    const [sharedReports, setSharedReports] = useState<(SaveSharedReportResponse & { dataset_name?: string })[]>([])
    useEffect(() => {
        if (!user?.email) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await sharedReportService.getSharedWithMe(50, 0);
                if (cancelled) return;
                const enriched = await Promise.all(
                    res.reports.map(async (r) => {
                        try {
                            const detail = await sharedReportService.getSharedReport(r.report_id);
                            const raw = detail.raw_report as Record<string, unknown>;
                            const desc = raw.data_object_description as Record<string, unknown> | undefined;
                            const name = (raw.dataset_name as string) || (raw.data_object_name as string) || (desc?.dataset_name as string) || (desc?.table_name as string) || undefined;
                            return { ...r, dataset_name: name };
                        } catch { return { ...r }; }
                    })
                );
                if (!cancelled) setSharedReports(enriched);
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [user?.email]);

    // Prevent body scrolling when search dialog is open
    useEffect(() => {
        if (isSearchFocused) {
            // Store current scroll position
            const scrollY = window.scrollY;
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
        } else {
            // Restore scroll position
            const scrollY = document.body.style.top;
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            if (scrollY) {
                window.scrollTo(0, parseInt(scrollY || '0') * -1);
            }
        }

        // Cleanup on unmount
        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
        };
    }, [isSearchFocused]);

    // Close search dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
                setIsSearchFocused(false)
            }
        }

        if (isSearchFocused) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isSearchFocused])

    // Filter data sources based on search query
    // Handle opening report directly for a data object
    const handleDataObjectClick = async (dataObject: DataSource) => {
        try {
            // First, try to get the latest historical report for this data object
            const { authenticatedHistoricalReportService } = await import('../services/historicalReportService.backend');

            const reports = await authenticatedHistoricalReportService.listReports({
                data_object_id: dataObject.uuid,
                order_by: 'generated_at',
                order_direction: 'desc',
                limit: 1,
                status: 'analyzed'
            });

            if (reports.reports && reports.reports.length > 0) {
                // Open the latest historical report in a new tab
                const latestReport = reports.reports[0];
                const reportUrl = `/report?reportId=${encodeURIComponent(latestReport.id)}`;
                window.open(reportUrl, '_blank');
            } else {
                // Open live analysis in a new tab if no historical reports found
                const reportUrl = `/report?dataObjectName=${encodeURIComponent(dataObject.name)}&dataObjectUuid=${encodeURIComponent(dataObject.uuid)}`;
                window.open(reportUrl, '_blank');
            }
        } catch (error) {
            console.error('Error fetching historical reports:', error);
            // Open live analysis in a new tab on error
            const reportUrl = `/report?dataObjectName=${encodeURIComponent(dataObject.name)}&dataObjectUuid=${encodeURIComponent(dataObject.uuid)}`;
            window.open(reportUrl, '_blank');
        }

        setIsSearchFocused(false); // Close dropdown
    }

    const navigation = [
        { name: 'History', href: '/reports', current: location.pathname === '/reports' }
    ]

    const isEntry = location.pathname === '/';

    return (
        <>
            <div className="min-h-screen bg-white">
                {/* Background Blur Overlay when search is focused */}
                {isSearchFocused && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-20 z-30"
                        style={{
                            animation: 'smoothBackdrop 0.2s ease-out forwards',
                            backdropFilter: 'blur(8px)'
                        }}
                        onClick={() => {
                            setIsSearchFocused(false);
                            setSearchQuery('');
                        }}
                    />
                )}

                {/* Navigation Header */}
                {!isEntry && (
                    <nav className="shadow-xl border-b border-brand-800/20 sticky top-0 z-fixed backdrop-blur-md" style={{ background: 'linear-gradient(135deg, #F68B1F 0%, #DE1825 50%, #8B0027 100%)' }}>
                        <div className="w-full">
                            <div className="flex justify-between items-center h-16 min-w-0" style={{ paddingLeft: 'clamp(0.5rem, 2vw, 2rem)', paddingRight: 'clamp(0.5rem, 2vw, 2rem)' }}>
                                {/* Left Side: RICH Logo | Data Doctor | Version */}
                                <div className="flex items-center space-x-0.5 sm:space-x-1 flex-shrink-0 min-w-0">
                                    {/* RICH Logo */}
                                    <div className="flex-shrink-0">
                                        <img
                                            src="/RICH White Logo-01 1.svg"
                                            alt="RICH Logo"
                                            className="h-8 sm:h-10 w-auto"
                                        />
                                    </div>

                                    {/* Data Doctor Button */}
                                    <div
                                        onClick={() => {
                                            navigate('/', { replace: true, state: { fromHeader: true } });
                                        }}
                                        title={t('Home')}
                                        style={{
                                            cursor: 'pointer',
                                            display: 'inline-block'
                                        }}
                                        onMouseDown={(e) => {
                                            e.currentTarget.querySelector('img').style.transform = 'scale(0.95)';
                                            e.currentTarget.querySelector('img').style.opacity = '0.7';
                                        }}
                                        onMouseUp={(e) => {
                                            e.currentTarget.querySelector('img').style.transform = 'scale(1)';
                                            e.currentTarget.querySelector('img').style.opacity = '0.9';
                                        }}
                                    >
                                        <img
                                            src="/DD.svg"
                                            alt="Data Doctor"
                                            className="h-14 w-auto sm:h-[52px] sm:w-[95px]"
                                            style={{
                                                opacity: '0.9',
                                                transition: 'all 0.15s ease',
                                                display: 'block'
                                            }}
                                            onMouseEnter={(e) => {
                                                const target = e.target as HTMLElement;
                                                target.style.opacity = '1';
                                                target.style.transform = 'scale(1.02)';
                                            }}
                                            onMouseLeave={(e) => {
                                                const target = e.target as HTMLElement;
                                                target.style.opacity = '0.9';
                                                target.style.transform = 'scale(1)';
                                            }}
                                        />
                                    </div>

                                    {/* Version */}
                                    <button
                                        type="button"
                                        onClick={() => setShowVersion(true)}
                                        className="px-1.5 py-0.5 font-semibold text-red-700 rounded-full shadow-sm hover:opacity-80 transition-all duration-300 flex-shrink-0 hover:scale-105 text-[10px] sm:text-xs"
                                        style={{ backgroundColor: 'white', background: 'white', border: 'none' }}
                                        title={t('Version History')}
                                        aria-label={`Show application version ${APP_VERSION}`}
                                    >
                                        v{APP_VERSION}
                                    </button>
                                </div>

                                {/* Right Side: Search | History | Info | User */}
                                <div className="flex items-center space-x-3 sm:space-x-4 flex-shrink-0 min-w-0">
                                    {/* Search Button */}
                                    <div className="relative flex-shrink-0" ref={searchContainerRef}>
                                        <button
                                            type="button"
                                            title={t('Search Reports Here')}
                                            className="flex items-center justify-center transition-all duration-300 hover:scale-105 hover:shadow-lg group"
                                            style={{
                                                minWidth: 'clamp(220px, 30vw, 300px)',
                                                paddingTop: '4px',
                                                paddingBottom: '4px',
                                                paddingLeft: '24px',
                                                paddingRight: '24px',
                                                border: '2px solid transparent',
                                                backgroundColor: '#f3f4f6',
                                                color: '#374151',
                                                fontSize: 'clamp(12px, 1.5vw, 14px)',
                                                fontWeight: '400',
                                                borderRadius: '12px',
                                                gap: '8px',
                                                boxShadow: '0 3px 8px rgba(156, 163, 175, 0.2)'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#e5e7eb';
                                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(156, 163, 175, 0.3)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = '#f3f4f6';
                                                e.currentTarget.style.boxShadow = '0 3px 8px rgba(156, 163, 175, 0.2)';
                                            }}
                                            onClick={() => {
                                                setIsSearchFocused(!isSearchFocused);
                                                if (isSearchFocused) {
                                                    setSearchQuery('');
                                                } else {
                                                    setTimeout(() => {
                                                        const input = document.querySelector('.search-input-field') as HTMLInputElement;
                                                        if (input) input.focus();
                                                    }, 100);
                                                }
                                            }}
                                        >
                                            <FaSearchengin className="w-4 sm:w-5 h-4 sm:h-5 group-hover:scale-110 transition-transform duration-300" style={{ color: '#374151' }} />
                                            <span className="group-hover:tracking-wide transition-all duration-300">{t('Search Reports Here')}</span>
                                        </button>

                                        {/* Search Panel with Input and Results */}
                                        {isSearchFocused && (
                                            <div
                                                className="absolute right-0 mt-2 bg-white rounded-3xl shadow-xl border z-50"
                                                style={{
                                                    width: '500px',
                                                    top: '100%',
                                                    animation: 'smoothFadeSlide 0.3s ease-out forwards'
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {/* Search Input Header */}
                                                <div className="p-4 border-b bg-gray-50 rounded-t-3xl">
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            placeholder={t('Search reports here...')}
                                                            value={searchQuery}
                                                            onChange={(e) => setSearchQuery(e.target.value)}
                                                            className="search-input-field w-full px-4 py-3 pl-12 pr-20 text-sm border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                                                            style={{
                                                                backgroundColor: '#ffffff',
                                                                color: '#111827',
                                                                fontSize: '14px',
                                                                fontWeight: '500'
                                                            }}
                                                            autoFocus
                                                        />
                                                        <FaSearchengin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6" />

                                                        <div className="absolute right-12 top-1/2 transform -translate-y-1/2 text-xs text-gray-500 font-medium">
                                                            {(() => {
                                                                const query = searchQuery.toLowerCase().trim();
                                                                const filteredCount = allDataSources.filter((dataSource) => {
                                                                    if (!query) return true;

                                                                    const name = (dataSource.name || '').toLowerCase();
                                                                    const fileName = (dataSource.originalFileName || '').toLowerCase();
                                                                    const folder = dataSource.folderId
                                                                        ? folders.find(f => f.id === dataSource.folderId)
                                                                        : null;
                                                                    const folderName = folder ? folder.name.toLowerCase() : 'main screen';

                                                                    return name.includes(query) || fileName.includes(query) || folderName.includes(query);
                                                                }).length;
                                                                const sharedCount = sharedReports.filter(r => {
                                                                    if (!query) return true;
                                                                    return (r.dataset_name || '').toLowerCase().includes(query) || (r.shared_by_email || '').toLowerCase().includes(query);
                                                                }).length;

                                                                return `${filteredCount + sharedCount} found`;
                                                            })()}
                                                        </div>

                                                        {searchQuery && (
                                                            <button
                                                                onClick={() => setSearchQuery('')}
                                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 w-5 h-5 flex items-center justify-center"
                                                            >
                                                                <FaTimes className="w-3 h-3" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Search Results */}
                                                <div
                                                    className="search-results-container max-h-80 overflow-y-scroll"
                                                    style={{
                                                        scrollBehavior: 'smooth',
                                                        overflowY: 'scroll',
                                                        height: '320px'
                                                    }}
                                                >
                                                    {allDataSources.length === 0 && sharedReports.length === 0 ? (
                                                        <div className="p-6 text-center text-gray-500">
                                                            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                            </svg>
                                                            <p className="text-lg">{t('No data objects found')}</p>
                                                        </div>
                                                    ) : (() => {
                                                        const query = searchQuery.toLowerCase().trim();

                                                        // Build unified search items: data sources + shared reports
                                                        type SearchItem = { type: 'datasource'; data: DataSource; folder: string } | { type: 'shared'; data: SaveSharedReportResponse & { dataset_name?: string } };
                                                        const items: SearchItem[] = [];

                                                        allDataSources.forEach((ds) => {
                                                            const name = (ds.name || '').toLowerCase();
                                                            const fileName = (ds.originalFileName || '').toLowerCase();
                                                            const folder = ds.folderId ? folders.find(f => f.id === ds.folderId) : null;
                                                            const folderName = folder ? folder.name : 'Main Screen';
                                                            if (!query || name.includes(query) || fileName.includes(query) || folderName.toLowerCase().includes(query)) {
                                                                items.push({ type: 'datasource', data: ds, folder: folderName });
                                                            }
                                                        });

                                                        sharedReports.forEach((r) => {
                                                            const name = (r.dataset_name || '').toLowerCase();
                                                            const sharer = (r.shared_by_email || '').toLowerCase();
                                                            if (!query || name.includes(query) || sharer.includes(query)) {
                                                                items.push({ type: 'shared', data: r });
                                                            }
                                                        });

                                                        if (items.length === 0) {
                                                            return (
                                                                <div className="p-6 text-center text-gray-500">
                                                                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                                    </svg>
                                                                    <p className="text-lg">No reports found for "{searchQuery}"</p>
                                                                    <p className="text-sm text-gray-400 mt-1">{t('Try a different search term')}</p>
                                                                </div>
                                                            );
                                                        }

                                                        return (
                                                            <div className="py-3">
                                                                <div className="p-4 space-y-3">
                                                                    {items.map((item) => {
                                                                        if (item.type === 'datasource') {
                                                                            const dataSource = item.data;
                                                                            return (
                                                                                <div
                                                                                    key={`ds-${dataSource.id}`}
                                                                                    tabIndex={0}
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                    className="search-card bg-white border border-gray-200 rounded-2xl p-4 hover:bg-gray-50 hover:border-gray-300 hover:shadow-lg focus:bg-blue-50 focus:border-blue-400 focus:outline-none transform hover:scale-102 transition-all duration-200 group"
                                                                                >
                                                                                    <div className="flex items-center space-x-4">
                                                                                        <div className="flex-shrink-0">
                                                                                            <FileIcon fileName={dataSource.originalFileName || ''} className="w-12 h-12" />
                                                                                        </div>
                                                                                        <div className="flex-1 min-w-0">
                                                                                            <h4 className="text-base font-medium text-gray-900 group-hover:text-blue-600 truncate notranslate">
                                                                                                {dataSource.name}
                                                                                            </h4>
                                                                                            <div className="mt-1">
                                                                                                <span className="text-sm text-gray-500 truncate block notranslate">
                                                                                                    {dataSource.originalFileName}
                                                                                                </span>
                                                                                            </div>
                                                                                            <div className="mt-2">
                                                                                                <span className="text-sm text-gray-600 truncate block flex items-center">
                                                                                                    <AiFillFolderOpen className="w-4 h-4 mr-2" /> {item.folder}
                                                                                                </span>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="flex-shrink-0">
                                                                                            <button
                                                                                                onMouseDown={(e) => e.preventDefault()}
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    handleDataObjectClick(dataSource);
                                                                                                    setIsSearchFocused(false);
                                                                                                    setSearchQuery('');
                                                                                                }}
                                                                                                className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-full transition-all duration-300 transform hover:scale-105"
                                                                                            >
                                                                                                <TbFolderShare className="w-5 h-5" />
                                                                                                <span>{t('Open')}</span>
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        }
                                                                        // Shared report card — same style as data source cards
                                                                        const report = item.data;
                                                                        return (
                                                                            <div
                                                                                key={`shared-${report.share_id}`}
                                                                                tabIndex={0}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                className="search-card bg-white border border-gray-200 rounded-2xl p-4 hover:bg-gray-50 hover:border-gray-300 hover:shadow-lg focus:bg-blue-50 focus:border-blue-400 focus:outline-none transform hover:scale-102 transition-all duration-200 group"
                                                                            >
                                                                                <div className="flex items-center space-x-4">
                                                                                    <div className="flex-shrink-0">
                                                                                        <TbReportAnalytics className="w-12 h-12 text-red-600" />
                                                                                    </div>
                                                                                    <div className="flex-1 min-w-0">
                                                                                        <h4 className="text-base font-medium text-gray-900 group-hover:text-blue-600 truncate notranslate">
                                                                                            {report.dataset_name || t('Shared Report')}
                                                                                        </h4>
                                                                                        <div className="mt-1">
                                                                                            <span className="text-sm text-gray-500 truncate block">
                                                                                                {t('Shared by')} {report.shared_by_email.split('@')[0]}
                                                                                            </span>
                                                                                        </div>
                                                                                        <div className="mt-2">
                                                                                            <span className="text-sm text-gray-600 truncate block flex items-center">
                                                                                                <HiShare className="w-4 h-4 mr-2" /> {report.issue_count} {t('issues')}
                                                                                            </span>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="flex-shrink-0">
                                                                                        <button
                                                                                            onMouseDown={(e) => e.preventDefault()}
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                navigate(`/shared-report?reportId=${report.report_id}`);
                                                                                                setIsSearchFocused(false);
                                                                                                setSearchQuery('');
                                                                                            }}
                                                                                            className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-full transition-all duration-300 transform hover:scale-105"
                                                                                        >
                                                                                            <TbFolderShare className="w-5 h-5" />
                                                                                            <span>{t('Open')}</span>
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* History Button */}
                                    {navigation.map((item) => (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            className="w-7 sm:w-9 h-7 sm:h-9 rounded-full hover:opacity-80 transition-all duration-300 flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: 'transparent', background: 'transparent', border: 'none' }}
                                            title={t('History')}
                                        >
                                            <FaHistory className="w-5 sm:w-7 h-5 sm:h-7 text-white transition-transform hover:scale-105" style={{ fontSize: 'clamp(16px, 2vw, 26px)', color: 'white' }} />
                                        </Link>
                                    ))}

                                    {/* Training Button */}
                                    <Link
                                        to="/training"
                                        className="w-7 sm:w-9 h-7 sm:h-9 rounded-full hover:opacity-80 transition-all duration-300 flex items-center justify-center flex-shrink-0"
                                        style={{ backgroundColor: 'transparent', background: 'transparent', border: 'none' }}
                                        title={t('Training')}
                                    >
                                        <svg className="w-5 sm:w-7 h-5 sm:h-7 transition-transform hover:scale-105" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect width="28" height="28" rx="4" fill="white" />
                                            <path d="M19.3167 15.3416C20.4223 14.7889 20.4223 13.2111 19.3167 12.6584L12.1708 9.08541C11.1735 8.58673 10 9.31198 10 10.4271V17.5729C10 18.688 11.1735 19.4133 12.1708 18.9146L19.3167 15.3416Z" fill="#9B0527" />
                                        </svg>
                                    </Link>

                                    {/* Admin Button - only visible to admin group members */}
                                    {isAdmin && (
                                        <Link
                                            to="/admin"
                                            className="w-7 sm:w-9 h-7 sm:h-9 rounded-full hover:opacity-80 transition-all duration-300 flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: 'transparent', background: 'transparent', border: 'none' }}
                                            title={t('Admin Panel')}
                                        >
                                            <FaUserShield className="w-5 sm:w-7 h-5 sm:h-7 text-white transition-transform hover:scale-105" style={{ fontSize: 'clamp(16px, 2vw, 26px)', color: 'white' }} />
                                        </Link>
                                    )}

                                    {/* Info Button */}
                                    <button
                                        className="w-7 sm:w-9 h-7 sm:h-9 rounded-full hover:opacity-80 transition-all duration-300 flex items-center justify-center flex-shrink-0"
                                        style={{ backgroundColor: 'transparent', background: 'transparent', border: 'none' }}
                                        title={t('AI Disclaimer')}
                                        onClick={() => setShowDisclaimer(true)}
                                    >
                                        <TbMessageReportFilled className="w-6 sm:w-8 h-6 sm:h-8 text-white transition-transform hover:scale-105" style={{ fontSize: 'clamp(20px, 2.5vw, 32px)', color: 'white' }} />
                                    </button>

                                    {/* User Profile */}
                                    <div className="flex-shrink-0">
                                        <UserProfile />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </nav>
                )}

                {/* Main Content Area */}
                <main className={isEntry ? '' : 'w-full py-4'} style={isEntry ? {} : { backgroundColor: '#fef7f7', minHeight: '100vh' }}>
                    <div className="layout-stack" style={{ paddingLeft: 'clamp(0.5rem, 2vw, 2rem)', paddingRight: 'clamp(0.5rem, 2vw, 2rem)' }}>
                        {children}
                    </div>
                </main>
            </div>

            <DisclaimerModal isOpen={showDisclaimer} onClose={() => setShowDisclaimer(false)} />
            <VersionHistoryPanel isOpen={showVersion} onClose={() => setShowVersion(false)} />
        </>
    )
}

export default Layout